import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-forest flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="bg-bone border border-forest/20 p-10 shadow-2xl relative">
          <div className="absolute -inset-0 translate-x-3 translate-y-3 bg-terra/70 -z-10" />
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo-mark.png" alt="Learning Lights Initiative logo" className="h-12 w-auto" />
            <span className="font-sans font-extrabold text-lg text-forest uppercase leading-tight">Learning Lights<br /><span className="text-xs font-semibold tracking-[0.3em] text-moss">Admin</span></span>
          </div>

          <h1 className="font-serif font-bold tracking-tighter text-forest text-3xl" data-testid="admin-login-heading">
            Staff <span className="italic text-terra">sign in.</span>
          </h1>
          <p className="text-sm text-moss mt-2 mb-8">Manage journal stories and view submissions.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <input
              required type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra focus:ring-1 focus:ring-terra transition"
              data-testid="admin-login-email-input"
            />
            <input
              required type="password" placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra focus:ring-1 focus:ring-terra transition"
              data-testid="admin-login-password-input"
            />
            {error && (
              <p className="text-sm font-semibold text-destructive" data-testid="admin-login-error">{error}</p>
            )}
            <button type="submit" disabled={busy} data-testid="admin-login-submit-btn"
              className="w-full bg-forest text-bone font-bold py-4 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
        <Link to="/" data-testid="admin-login-back-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-bone/70 hover:text-terra transition-colors">
          <ArrowLeft size={15} /> Back to the site
        </Link>
      </motion.div>
    </div>
  );
}
